import { Temporal } from 'temporal-polyfill';

import type { BaseEntityTableGroupKey } from '@/components/table/entity-table.js';

export type GroupSelectionState = boolean | 'indeterminate';

/** None selected, every row selected, or a partial selection for a group checkbox. */
export function getGroupSelectionState(isSelected: readonly boolean[]): GroupSelectionState {
  const selectedCount = isSelected.filter(Boolean).length;
  if (selectedCount === 0) return false;
  if (selectedCount === isSelected.length) return true;
  return 'indeterminate';
}

export function visibleGroupedRowCount<Row>(
  rows: readonly Row[],
  getGroupKey: (row: Row) => { key: string },
  collapsedKeys: readonly string[],
): number {
  if (collapsedKeys.length === 0) return rows.length;
  const collapsed = new Set(collapsedKeys);
  let visible = 0;
  for (const row of rows) {
    if (!collapsed.has(getGroupKey(row).key)) visible += 1;
  }
  return visible;
}

export function needsMoreVisibleRows({
  visibleRowCount,
  pageSize,
  loadedRowCount,
  totalRowCount,
}: {
  visibleRowCount: number;
  pageSize: number;
  loadedRowCount: number;
  totalRowCount: number;
}): boolean {
  if (loadedRowCount >= totalRowCount) return false;
  return visibleRowCount < pageSize;
}

/** Collapsed keys only. An empty list means every section is expanded. */
export function toggleCollapsedGroupKey(collapsedKeys: readonly string[], groupKey: string): string[] {
  if (collapsedKeys.includes(groupKey)) {
    return collapsedKeys.filter((key) => key !== groupKey);
  }
  return [...collapsedKeys, groupKey];
}

/** Selects or clears one group's row ids without changing selection outside the group. */
export function withGroupRowsSelected(
  rowSelection: Readonly<Record<string, boolean>>,
  rowIds: readonly string[],
  selected: boolean,
): Record<string, boolean> {
  const next = { ...rowSelection };
  for (const rowId of rowIds) {
    if (selected) {
      next[rowId] = true;
    } else {
      delete next[rowId];
    }
  }
  return next;
}

export interface StartDateGroupKey extends BaseEntityTableGroupKey {
  instant: Temporal.Instant;
  plainDate: Temporal.PlainDate;
  timeZone: string;
}

export function groupByStartDate(startInstant: Temporal.Instant, timeZone: string): StartDateGroupKey {
  const zoned = startInstant.toZonedDateTimeISO(timeZone);
  const plainDate = zoned.toPlainDate();
  return {
    key: plainDate.toString(),
    instant: plainDate.toZonedDateTime(timeZone).toInstant(),
    plainDate,
    timeZone,
  };
}

export interface StartDateTimeRangeGroupKey extends BaseEntityTableGroupKey {
  startInstant: Temporal.Instant;
  endInstant: Temporal.Instant;
  plainDate: Temporal.PlainDate;
  startTime: Temporal.PlainTime;
  endTime: Temporal.PlainTime;
  timeZone: string;
}

export interface SchoolNameGroupKey extends BaseEntityTableGroupKey {
  schoolName: string;
}

export function groupBySchoolName(schoolName: string | undefined): SchoolNameGroupKey {
  const name = schoolName ?? 'Unknown';
  return {
    key: name,
    schoolName: name,
  };
}

export function groupByStartDateAndTimeRange(
  startInstant: Temporal.Instant,
  endInstant: Temporal.Instant,
  timeZone: string,
): StartDateTimeRangeGroupKey {
  const startZoned = startInstant.toZonedDateTimeISO(timeZone);
  const endZoned = endInstant.toZonedDateTimeISO(timeZone);
  const plainDate = startZoned.toPlainDate();
  const startTime = startZoned.toPlainTime();
  const endTime = endZoned.toPlainTime();
  return {
    key: `${plainDate.toString()}_${startTime.toString()}_${endTime.toString()}`,
    startInstant,
    endInstant,
    plainDate,
    startTime,
    endTime,
    timeZone,
  };
}
